import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { AdminFormRow } from "./types";

type Context = {
  previousList: AdminFormRow[] | undefined;
  previousDetail: AdminFormRow | undefined;
};

// POST /api/admin/forms/:id/publish
//
// Pattern C state-flip — optimistically flips status to "published" in detail
// and list cache.
export function usePublishForm() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, string, Context>({
    mutationFn: (id) =>
      apiFetch<{ ok: true }>(`/admin/forms/${id}/publish`, { method: "POST" }),
    onMutate: async (id) => {
      const listKey = queryKeys.adminForms.list();
      const detailKey = queryKeys.adminForms.detail(id);
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousList = queryClient.getQueryData<AdminFormRow[]>(listKey);
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow[]>(listKey, (old) =>
        old?.map((row) =>
          row.id === id ? { ...row, status: "published" } : row,
        ),
      );
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) =>
        old ? { ...old, status: "published" } : old,
      );
      return { previousList, previousDetail };
    },
    onError: (err, id, context) => {
      if (context?.previousList !== undefined) {
        queryClient.setQueryData(queryKeys.adminForms.list(), context.previousList);
      }
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(queryKeys.adminForms.detail(id), context.previousDetail);
      }
      toast.error(err.message ?? "Failed to publish form");
    },
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminForms.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminForms.detail(id) });
    },
  });
}
